import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./context/LangContext";
import { ThemeProvider } from "./context/ThemeContext";
import BetaLanding from "./pages/Landing";
import BetaLogin from "./pages/Login";
import BetaRegister from "./pages/Register";
import BetaDashboard from "./pages/Dashboard";
import BetaFoodDatabase from "./pages/FoodDatabase";
import BetaMealMixer from "./pages/MealMixer";
import BetaHistory from "./pages/History";
import BetaSettings from "./pages/Settings";
import BetaDialysisAssistant from "./pages/DialysisAssistant";
import BetaAdmin from "./pages/Admin";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import MedicalDisclaimer from "./pages/MedicalDisclaimer";
import Layout from "./components/Layout";
import { V2SessionProvider } from "./v2/context/V2SessionContext";
import { V2AdminProtected, V2Protected, V2PublicOnly } from "./v2/components/V2RouteGuards";
import V2Landing from "./v2/pages/Landing";
import V2Login from "./v2/pages/Login";
import V2Register from "./v2/pages/Register";
import V2GuestEntry from "./v2/pages/GuestEntry";
import V2ForgotPassword from "./v2/pages/ForgotPassword";
import V2CompleteRegistration from "./v2/pages/CompleteRegistration";
import V2ResetPassword from "./v2/pages/ResetPassword";
import V2Dashboard from "./v2/pages/Dashboard";
import V2Foods from "./v2/pages/Foods";
import V2AddCustomFood from "./v2/pages/AddCustomFood";
import V2FoodLogging from "./v2/pages/FoodLogging";
import V2History from "./v2/pages/History";
import V2Mixer from "./v2/pages/Mixer";
import V2Reminders from "./v2/pages/Reminders";
import V2Settings from "./v2/pages/Settings";
import V2Assistant from "./v2/pages/Assistant";
import V2Admin from "./v2/pages/Admin";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user && !guestMode) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <V2SessionProvider>
            <BrowserRouter>
              <Toaster position="top-center" richColors />
              <Routes>
                <Route path="/" element={<V2Landing />} />
                <Route path="/login" element={<V2PublicOnly><V2Login /></V2PublicOnly>} />
                <Route path="/register" element={<V2PublicOnly><V2Register /></V2PublicOnly>} />
                <Route path="/guest" element={<V2PublicOnly><V2GuestEntry /></V2PublicOnly>} />
                <Route path="/forgot-password" element={<V2ForgotPassword />} />
                <Route path="/complete-registration" element={<V2CompleteRegistration />} />
                <Route path="/reset-password" element={<V2ResetPassword />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/medical-disclaimer" element={<MedicalDisclaimer />} />
                <Route path="/dashboard" element={<V2Protected><V2Dashboard /></V2Protected>} />
                <Route path="/foods" element={<V2Protected><V2Foods /></V2Protected>} />
                <Route path="/foods/add" element={<V2Protected><V2AddCustomFood /></V2Protected>} />
                <Route path="/add-custom-food" element={<Navigate to="/foods/add" replace />} />
                <Route path="/food-logging" element={<V2Protected><V2FoodLogging /></V2Protected>} />
                <Route path="/history" element={<V2Protected><V2History /></V2Protected>} />
                <Route path="/mixer" element={<V2Protected><V2Mixer /></V2Protected>} />
                <Route path="/meal-mixer" element={<Navigate to="/mixer" replace />} />
                <Route path="/reminders" element={<V2Protected><V2Reminders /></V2Protected>} />
                <Route path="/settings" element={<V2Protected><V2Settings /></V2Protected>} />
                <Route path="/assistant" element={<V2Protected><V2Assistant /></V2Protected>} />
                <Route path="/admin" element={<V2AdminProtected><V2Admin /></V2AdminProtected>} />
                <Route path="/beta" element={<Navigate to="/beta/dashboard" replace />} />
                <Route path="/beta/landing" element={<BetaLanding />} />
                <Route path="/beta/login" element={<PublicOnly><BetaLogin /></PublicOnly>} />
                <Route path="/beta/register" element={<PublicOnly><BetaRegister /></PublicOnly>} />
                <Route path="/beta/dashboard" element={<Protected><BetaDashboard /></Protected>} />
                <Route path="/beta/foods" element={<Protected><BetaFoodDatabase /></Protected>} />
                <Route path="/beta/meal-mixer" element={<Protected><BetaMealMixer /></Protected>} />
                <Route path="/beta/history" element={<Protected><BetaHistory /></Protected>} />
                <Route path="/beta/settings" element={<Protected><BetaSettings /></Protected>} />
                <Route path="/beta/assistant" element={<Protected><BetaDialysisAssistant /></Protected>} />
                <Route path="/beta/admin" element={<Protected adminOnly><BetaAdmin /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </V2SessionProvider>
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}

export default App;
